import {
  getFacebookPagesLoading,
  getFacebookPagesSuccess,
  getFacebookPagesFailure,
  getInstagramIdsLoading,
  getInstagramIdsSuccess,
  getInstagramIdsFailure,
  getBusinessManagerIdLoading,
  getBusinessManagerIdSuccess,
  getBusinessManagerIdFailure,
  getInstagramAccountDetailsLoading,
  getInstagramAccountDetailsSuccess,
  getInstagramAccountDetailsFailure,
  uploadAdImageStart,
  uploadAdImageFailure,
  uploadAdImageSuccess,
  uploadAdVideoStart,
  uploadAdVideoSuccess,
  uploadAdVideoFailure,
  clearInstagramAccountDetails,
} from "@redux/slices/facebookPagesSlice.js"
import { getFacebookPages } from "@services/getFacebookPages"
import { useDispatch } from "react-redux"
import {
  getPBIAIds,
  getInstagramIdsFromFBPageId,
} from "@services/getInstagramIds"
import { getFacebookBusinessManagerId } from "@services/getFacebookBusinessManagerId"
import { getInstagramAccountDetails } from "@services/getInstagramAccountDetails"
import { uploadFacebookAdImage } from "../services/uploadFacebookAdImage"
import { getCdnCreativeImages } from "@services/getCdnCreativeImages"
import { removeBase64Prefix } from "@utils/removeBase64prefix"
import {
  uploadFacebookAdVideos,
  getVideoStatus,
} from "@services/facebook/uploadFacebookAdVideos"
import { getVideoThumbnailUrl } from "@services/facebook/fetchFacebookAdVideoThumbnail"

const useFacebookPages = () => {
  const dispatch = useDispatch()

  const handleGetFacebookPages = async () => {
    try {
      dispatch(getFacebookPagesLoading())
      const response = await getFacebookPages()
      dispatch(getFacebookPagesSuccess(response.data.data))
    } catch (error) {
      dispatch(getFacebookPagesFailure())
      console.log(error)
    }
  }

  // const handleGetInstagramIds = async (facebookPageId, fbPages=null) => {
  //   try {
  //     dispatch(getInstagramIdsLoading())
  //     let combinedInstagramIds = []
  //     const response = await getInstagramIds(facebookPageId)
  //     combinedInstagramIds = response.data?.data
  //     console.log("combinedInstagramIds after getInstagramIds:", combinedInstagramIds)
  //     // const lengthOfInstagramIds = response.data?.data.length
  //     // if (lengthOfInstagramIds === 0 && fbPages) {

  //     const pbiaIds = await handleGetAllPBIAIds(fbPages)
  //     combinedInstagramIds = [...combinedInstagramIds, ...pbiaIds]
  //     console.log("combinedInstagramIds after pbiaIds:", combinedInstagramIds)
  //     dispatch(getInstagramIdsSuccess(combinedInstagramIds))
  //     // }
  //     // else {
  //     //   console.log("response from instagram ids:", response.data?.data)
  //     //   dispatch(getInstagramIdsSuccess(response.data?.data))
  //     // }
  //   } catch (error) {
  //     dispatch(getInstagramIdsFailure())
  //     console.error("Failed to fetch Instagram IDs:", error)
  //   }
  // }

  const handleGetInstagramIds = async (fbPages) => {
    // loop over all fbPages, first try to get insta id from that fbpage if not then get insta id from pbia
    try {
      dispatch(getInstagramIdsLoading())
      let combinedInstagramIds = []
      for (const page of fbPages) {
        const response = await getInstagramIdsFromFBPageId(page.id)

        const instaId = response.data?.connected_instagram_account?.id

        if (instaId) {
          combinedInstagramIds.push({
            id: instaId,
            fbPageId: page.id,
            fbPageName: page.name,
          })
        } else {
          const pbiaResponse = await handleGetPBIAIds(
            page.id,
            page.access_token
          )
          if (pbiaResponse) {
            combinedInstagramIds.push({
              id: pbiaResponse.id,
              name: page.name,
              fbPageId: page.id,
              fbPageName: page.name,
            })
          }
        }
      }
      dispatch(getInstagramIdsSuccess(combinedInstagramIds))
    } catch (error) {
      dispatch(getInstagramIdsFailure())
      console.error("Failed to fetch Instagram IDs:", error)
    }
  }

  const handleGetBusinessManagerId = async () => {
    try {
      dispatch(getBusinessManagerIdLoading())
      const response = await getFacebookBusinessManagerId()
      dispatch(getBusinessManagerIdSuccess(response.data.data))
    } catch (error) {
      dispatch(getBusinessManagerIdFailure())
      console.error("Failed to fetch Business Manager ID:", error)
    }
  }

  const handleGetInstagramAccountDetails = async (instagramAccountId) => {
    try {
      dispatch(getInstagramAccountDetailsLoading())
      const response = await getInstagramAccountDetails(instagramAccountId)
      dispatch(getInstagramAccountDetailsSuccess(response.data))
    } catch (error) {
      dispatch(getInstagramAccountDetailsFailure())
      console.error("Failed to fetch Instagram account details:", error)
    }
  }

  const handleGetCdnCreativeImages = async (imgLink) => {
    try {
      const response = await getCdnCreativeImages(imgLink)
      const blob = response.data
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(removeBase64Prefix(reader.result))
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })
    } catch (error) {
      console.error("Failed to fetch CDN creative images:", error)
      throw error
    }
  }

  const handleUploadAdImage = async (adAccountId, imageLink, fileName) => {
    try {
      const formData = await handleGetCdnCreativeImages(imageLink)
      dispatch(uploadAdImageStart())

      if (formData) {
        const response = await uploadFacebookAdImage(
          adAccountId,
          formData,
          fileName
        )
        // console.log("response from upload image:", response)
        // return response.data.images.bytes.hash
        const fileHash = response.data.images[fileName]?.hash
        return fileHash
      }
      dispatch(uploadAdImageSuccess())
    } catch (error) {
      dispatch(uploadAdImageFailure(error.message))
      throw error
    }
  }

  const handleVideoUploadReadyStatusCheck = async (videoId) => {
    const MAX_ATTEMPTS = 10 // 5 minutes total (30 seconds * 10)
    const POLLING_INTERVAL = 30000 // 30 seconds
    let attempts = 0

    const checkStatus = async () => {
      try {
        const response = await getVideoStatus(videoId)
        const status = response.data?.status?.video_status

        if (status === "ready") {
          return true
        }

        if (status === "error") {
          throw new Error("Video processing failed")
        }

        if (attempts >= MAX_ATTEMPTS) {
          throw new Error("Video processing timeout")
        }

        // If still processing, wait and try again
        attempts++
        await new Promise((resolve) => setTimeout(resolve, POLLING_INTERVAL))
        return checkStatus()
      } catch (error) {
        console.error(`Video status check failed (attempt ${attempts}):`, error)
        throw error
      }
    }

    return checkStatus()
  }

  const handleUploadAdVideos = async (adAccountId, videoLink, fileName) => {
    try {
      dispatch(uploadAdVideoStart())
      const response = await uploadFacebookAdVideos(
        adAccountId,
        videoLink,
        fileName
      )
      const videoId = response.data.id

      // check if the video is ready to be used
      const statusResp = await handleVideoUploadReadyStatusCheck(videoId)
      if (statusResp) {
        dispatch(uploadAdVideoSuccess())
      } else {
        throw new Error("Video is not ready to be used")
      }
      return videoId
    } catch (error) {
      dispatch(uploadAdVideoFailure(error.message))
      throw error
    }
  }

  const handleVideoThumbnailUrl = async (videoId) => {
    try {
      const response = await getVideoThumbnailUrl(videoId)
      // console.log("response from thumbnail:", response)
      return response.data.data[0].uri
    } catch (error) {
      console.error("Failed to fetch video thumbnail URL:", error)
      throw error
    }
  }

  const handleMultipleVideoThumbnailUrl = async (
    creatives,
    creativeMapVideoIds = null
  ) => {
    try {
      // console.log("in multi video get thumbnail creativeMapVideoIds:", creativeMapVideoIds)
      const creativeWithThumbnails = await Promise.all(
        creatives.map(async (creative) => {
          const videoId = Object.keys(creativeMapVideoIds).length
            ? creativeMapVideoIds[creative._id]
            : creative.facebookVideoId
          const response = await getVideoThumbnailUrl(videoId)
          // console.log("response from thumbnail:", response)
          const thumbnailUrl = response.data.data[0].uri
          const thumbnailWidth = response.data.data[0].width
          const thumbnailHeight = response.data.data[0].height
          const isSquare = thumbnailWidth === thumbnailHeight
          return {
            ...creative,
            thumbnailUrl,
            thumbnailWidth,
            thumbnailHeight,
            isSquare,
          }
        })
      )
      return creativeWithThumbnails
    } catch (error) {
      console.error("Failed to fetch video thumbnail URLs:", error)
      throw error
    }
  }

  const handleGetPBIAIds = async (facebookPageId, facebookAccessToken) => {
    try {
      const response = await getPBIAIds(facebookPageId, facebookAccessToken)
      console.log("response from PBIA:", response)
      return response.data?.data[0]
    } catch (error) {
      console.error("Failed to fetch PBIA IDs:", error)
    }
  }

  const handleGetAllPBIAIds = async (fbPages) => {
    try {
      let pbiaIds = []
      for (const page of fbPages) {
        let response = await handleGetPBIAIds(page.id, page.access_token)
        // add the fbPageId and fbPageName to the response
        response = {
          ...response,
          fbPageId: page.id,
          fbPageName: page.name,
        }
        pbiaIds.push(response)
      }
      return pbiaIds
    } catch (error) {
      console.error("Failed to fetch all PBIA Instagram IDs:", error)
    }
  }

  return {
    handleGetFacebookPages,
    handleGetInstagramIds,
    handleGetBusinessManagerId,
    handleGetInstagramAccountDetails,
    handleUploadAdImage,
    handleUploadAdVideos,
    handleVideoThumbnailUrl,
    handleMultipleVideoThumbnailUrl,
    handleClearInstagramAccountDetails: () => dispatch(clearInstagramAccountDetails()),
  }
}

export default useFacebookPages
